import KogModalSideComponent from '@/main/components/modal/ModalSide';
import { getDefaultOptionDrop, getTranslateByKey } from '@/main/services/utils';
import { isNil } from 'lodash';
import * as S from './styles';
import { Controller, useForm } from 'react-hook-form';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import { KeyValue } from '@/main/models/common';
import { getOptionsStatusActiveGeneral } from '@/main/services/data/data';
import { useCompanyTypeReducer } from '@/main/store/companyType';
import { useCountryReducer } from '@/main/store/country';
import { useCultureReducer } from '@/main/store/culture';
import { useSystemApplicationReducer } from '@/main/store/systemApplication';
import { useEffect, useMemo, useState } from 'react';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import { StatusActiveGeneralEnum } from '@/main/services/enum/StatusActiveGeneralEnum';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useInsurerReducer } from '@/main/store/insurer';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';
import { SystemGuidRequest } from '@/main/models/request/systemGuideRequest';
import { systemGuideApi } from '@/main/api';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import { toast } from 'react-toastify';
import { KOGAreaButtons } from '@/main/components/shared/styles';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { SystemGuideSearchGridResponse } from '@/main/models/response/systemGuideResponse';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';

const ID_BASE_COMPONENTS = 'id-registerSystemGuide';

const INITIAL_FORM_STATE_DEFAULT = {
  country: null as KeyValue | null,
  insurer: null as KeyValue | null,
  language: [] as KeyValue[] | null,
  systemApplication: null as KeyValue | null,
  whoCanSee: [] as KeyValue[] | null,
  status: null as KeyValue | null,
  defaultCompany: false as boolean
};
type IFormRegisterSystemGuid = typeof INITIAL_FORM_STATE_DEFAULT;

interface Props {
  open: boolean;
  systemGuide?: SystemGuideSearchGridResponse;
  refreshSearchGrid: () => void;
  closeSidebar: () => void;
}

function SystemGuideRegister({
  systemGuide,
  refreshSearchGrid,
  closeSidebar
}: Props) {
  const isEdit = !isNil(systemGuide);

  const systemApplicationReducer = useSystemApplicationReducer();
  const countryReducer = useCountryReducer();
  const companyTypeReducer = useCompanyTypeReducer();
  const optionsStatus = getOptionsStatusActiveGeneral();
  const { culture: cultureReducer } = useCultureReducer();
  const { insurers: insurerReducer } = useInsurerReducer();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [systemGuideFile, setSystemGuideFile] = useState<File[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<IFormRegisterSystemGuid>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE_DEFAULT
  });

  const defaultCompanyWatched = watch('defaultCompany');

  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const disabledInsurer = userIsInsurer();

  useEffect(() => {
    if (insurerReducer.data?.length === 1) {
      const defaultInsurer = insurerReducer.data[0];
      setValue('insurer', {
        key: defaultInsurer.key,
        value: defaultInsurer.value
      });
    }
  }, [countryReducer.countriesUser.data]);

  useEffect(() => {
    if (isNil(systemGuide)) return;

    setIsLoading(true);
    systemGuideApi
      .getDetails(systemGuide.guidSystemGuide)
      .then(response => {
        const result = response.data.result;

        if (!isNil(result)) {
          const insurerValue = insurerReducer.data?.find(
            x => x.key == result.guidInsurer
          );

          const statusRef = result.active
            ? StatusActiveGeneralEnum.Active
            : StatusActiveGeneralEnum.Inactive;
          const statusValue = optionsStatus.find(x => x.key == statusRef);

          setValue('insurer', insurerValue ?? null);
          setValue('status', statusValue ?? null);
          setValue('language', result.cultures);
          setValue('whoCanSee', result.whoCanSeeCompanyTypes);
          setValue('defaultCompany', result.defaultCompany);
          setValue('systemApplication', {
            key: result.idSystemApplication,
            value: result.systemApplicationName
          });

          const byteArray = Uint8Array.from(atob(result.file.base64), c =>
            c.charCodeAt(0)
          );

          const file = new File([byteArray], result.file.fileName, {
            type: 'application/pdf'
          });

          setSystemGuideFile([file]);
        }
      })
      .finally(() => setIsLoading(false));
  }, [systemGuide]);

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, []);

  useEffect(() => {
    if (defaultCompanyWatched) {
      setValue('insurer', null);
      setValue('whoCanSee', []);
    }
  }, [defaultCompanyWatched]);

  const optionDefaultStatus = useMemo(() => {
    const statusValue = optionsStatus.find(
      item => item.key === StatusActiveGeneralEnum.Active
    );

    return statusValue ?? null;
  }, [optionsStatus]);

  const optionDefaultSystemApplication = useMemo(() => {
    const resultData = systemApplicationReducer.systemApplication.data;

    if (isNil(resultData)) return null;

    const findClaims = resultData.find(
      item => item.key === SystemApplicationEnum.Claims.toString()
    );

    return findClaims ?? null;
  }, [systemApplicationReducer.systemApplication.data]);

  const optionWhoCanSee = useMemo(() => {
    const companyTypeIgnore = [CompanyTypeEnum.Admin];
    const resultCompanyTypes = companyTypeReducer.companyType.data;

    if (isNil(resultCompanyTypes) || resultCompanyTypes.length == 0) {
      return [];
    }

    return (
      resultCompanyTypes
        .filter(x => !companyTypeIgnore.includes(Number(x.key)))
        .map(item => ({
          key: item.key,
          value: item.value
        })) || []
    );
  }, [
    companyTypeReducer.companyType.data,
    companyTypeReducer.companyType.isLoading
  ]);

  const optionLanguage = useMemo(() => {
    const resultLanguages = cultureReducer.data;

    if (isNil(resultLanguages) || resultLanguages.length == 0) {
      return [];
    }

    return (
      resultLanguages.map(item => ({
        key: item.key,
        value: item.value
      })) || []
    );
  }, [cultureReducer.data, cultureReducer.isLoading]);

  function resetForm() {
    reset(INITIAL_FORM_STATE_DEFAULT);

    setValue('insurer', null);
    setValue('language', null);
    setValue('systemApplication', null);
    setValue('whoCanSee', null);
    setValue('status', null);
    setValue('defaultCompany', false);

    setIsLoading(false);
    setSystemGuideFile([]);

    clearErrors();
  }

  function isDisabledCountry() {
    return countryReducer.countriesUser.data?.length === 1;
  }

  async function onSubmitSave(data: IFormRegisterSystemGuid) {
    const languages =
      !isNil(data.language) && data.language.length > 0
        ? data.language.map(x => String(x.key))
        : [];

    const whoCanSeeCompanyTypeIds =
      !isNil(data.whoCanSee) && data.whoCanSee.length > 0
        ? data.whoCanSee.map(x => Number(x.key))
        : [];

    const request: SystemGuidRequest = {
      ...(isEdit && { guid: systemGuide.guidSystemGuide }),
      guidCountry: data.country?.key?.toString() ?? '',
      idSystemApplication: Number(data.systemApplication?.key),
      guidCulture: languages,
      guidInsurer: data.insurer?.key?.toString(),
      active: Number(data.status?.key) == StatusActiveGeneralEnum.Active,
      whoCanSeeCompanyTypeIds: whoCanSeeCompanyTypeIds,
      defaultCompany: data.defaultCompany
    };

    setIsLoading(true);

    systemGuideApi
      .save(request, systemGuideFile[0])
      .then(() => {
        const messageSuccess = isEdit
          ? getTranslateByKey('information.systemGuideEditSuccessfully')
          : getTranslateByKey('information.systemGuideCreateSuccessfully');

        toast.success(messageSuccess);
        refreshSearchGrid();
        closeSidebar();
      })
      .finally(() => setIsLoading(false));
  }

  return (
    <form onSubmit={handleSubmit(onSubmitSave)} noValidate>
      <S.ContainerSidebar>
        <KogAutoCompleteControlled
          label={getTranslateByKey('information.country')}
          control={control}
          nameCustom="country"
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.country?.type === 'required'
              ? getTranslateByKey('information.countryRequired', true)
              : ''
          }
          hasErrorCustom={!!errors.country}
          options={countryReducer.countriesUser.data}
          isLoading={countryReducer.countriesUser.isLoading}
          onChangeCustom={() => {}}
          disabled={isDisabledCountry() || isLoading}
          autoHighlightCustom={countryReducer.countriesUser.data?.length === 1}
          setValueCustom={setValue}
          id={`${ID_BASE_COMPONENTS}-select-country`}
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS}-select-insurer`}
          nameCustom="insurer"
          label={getTranslateByKey('information.insurer')}
          rulesCustom={{ required: !defaultCompanyWatched }}
          isDefaultValueUnique={optionDefaultInsurerDrop !== null}
          control={control}
          setValueCustom={setValue}
          options={insurerReducer.data}
          isLoading={insurerReducer.isLoading}
          disabled={disabledInsurer || isLoading || defaultCompanyWatched}
          onChangeCustom={() => {}}
        />

        <KogInputAutoCompleteCheckControlled
          id={`${ID_BASE_COMPONENTS}-muiltselect-language`}
          label={getTranslateByKey('information.language')}
          name="language"
          control={control}
          setValueCustom={setValue}
          options={optionLanguage}
          isLoading={cultureReducer.isLoading}
          disabled={isLoading}
          onChangeCustom={() => {}}
          required
          controllerProps={{ rules: { required: true } }}
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS}-select-system`}
          label={getTranslateByKey('information.systemApplication')}
          control={control}
          nameCustom="systemApplication"
          rulesCustom={{ required: true }}
          errorMessageCustom=""
          hasErrorCustom={false}
          options={systemApplicationReducer.systemApplication.data}
          valueDefaultKey={optionDefaultSystemApplication?.key}
          isLoading={systemApplicationReducer.systemApplication.isLoading}
          disabled
          onChangeCustom={() => {}}
          setValueCustom={setValue}
        />

        <KogInputAutoCompleteCheckControlled
          id={`${ID_BASE_COMPONENTS}-txt-whoCanSee`}
          label={getTranslateByKey('information.whoCanSee')}
          name="whoCanSee"
          control={control}
          setValueCustom={setValue}
          options={optionWhoCanSee}
          isLoading={companyTypeReducer.companyType.isLoading}
          disabled={isLoading || defaultCompanyWatched}
          onChangeCustom={() => {}}
          required={!defaultCompanyWatched}
          controllerProps={{ rules: { required: !defaultCompanyWatched } }}
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS}-select-status`}
          nameCustom="status"
          label={getTranslateByKey('information.status')}
          control={control}
          rulesCustom={{ required: true }}
          setValueCustom={setValue}
          options={optionsStatus}
          valueDefaultKey={optionDefaultStatus?.key}
          disabled={isLoading}
          onChangeCustom={() => {}}
        />

        {userIsAdmin() && (
          <Controller
            name="defaultCompany"
            control={control}
            disabled={isLoading}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                label={
                  <div>
                    <span>
                      {getTranslateByKey(
                        'information.setTheSystemGuideAsDefault'
                      )}
                    </span>
                  </div>
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={value}
                    disabled={isLoading}
                    onChange={e => {
                      onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('insurer', null);
                      }
                    }}
                    id={`${ID_BASE_COMPONENTS}-checkbox-defaultCompany`}
                  />
                }
              />
            )}
          />
        )}

        <S.ContainerFile>
          <KOGFileUploader
            isFullScreenDragDrop
            isRequired
            files={systemGuideFile}
            setFiles={setSystemGuideFile}
            maxFiles={1}
            typeFiles={['pdf']}
            downloadFile={undefined}
            maxTotalSize={10}
          />
        </S.ContainerFile>

        <KOGAreaButtons style={{ justifyContent: 'center' }}>
          <SecondaryButton
            id={`${ID_BASE_COMPONENTS}-button-save`}
            type="submit"
            isLoading={isLoading}>
            {getTranslateByKey('information.save')}
          </SecondaryButton>
        </KOGAreaButtons>
      </S.ContainerSidebar>
    </form>
  );
}

export default function SystemGuideRegisterSidebar(props: Props) {
  if (!props.open) return null;

  const isEdit = !isNil(props.systemGuide);
  const titleSaveModal = getTranslateByKey('information.registerSystemGuide');
  const titleEditModal = getTranslateByKey('information.editSystemGuide');
  const title = isEdit ? titleEditModal : titleSaveModal;

  return (
    <KogModalSideComponent
      open={props.open}
      shouldRenderOpen
      title={title}
      closeModal={props.closeSidebar}>
      <SystemGuideRegister {...props} />
    </KogModalSideComponent>
  );
}
